<template>
  <component :is="layoutComponent">
    <div class="home">
      <section id="settings-compliance">
        <div class="container">
          <h6 class="mb-2 fw-bold">Compliance Documents</h6>
          <div class="card mb-3" v-if="showAddForm">
            <div class="card-body">
              <form @submit.prevent="save">
                <div class="row">
                  <div class="col-md-4 mb-2">
                    <label class="form-label">Document Name</label>
                    <input v-model="form.documentName" type="text" class="form-control" required />
                  </div>
                  <div class="col-md-4 mb-2">
                    <label class="form-label">Document Type</label>
                    <select v-model="form.documentType" class="form-control">
                      <option value="">Select document type</option>
                      <option v-for="dt in supplierDocumentTypes" :key="dt.id" :value="dt.name || dt.documentName || dt.id">
                        {{ dt.name || dt.documentName || dt.id }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-2 mb-2">
                    <label class="form-label">Expiry Date</label>
                    <input v-model="form.expiryDate" type="date" class="form-control" />
                  </div>
                  <div class="col-md-4 mb-2">
                    <label class="form-label">File URL / Path</label>
                    <input v-model="form.fileUrl" type="text" class="form-control" />
                  </div>

                  <div class="col-md-4 mb-2 mt-5" style="gap: 8px;">
                    <button type="button" class="btn btn-light" v-if="form.id" @click="resetForm">Cancel</button>
                    <button type="submit" class="btn btn-primary" :disabled="saving">
                      <span v-if="!saving">{{ form.id ? 'Update' : 'Add' }} Document</span>
                      <span v-else>Saving...</span>
                    </button>
                  </div>


                </div>

                
              </form>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="fw-bold mb-0">Registered Documents</h6>
                <button type="button" class="btn btn-sm btn-outline-primary" @click="showAddForm = !showAddForm">
                  <span v-if="!showAddForm">Add Document</span>
                  <span v-else>Hide</span>
                </button>
              </div>
              <table id="complianceDocsTable" class="table table-hover table-sm mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Expiry</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(doc, index) in documents" :key="doc.id" @click="edit(doc)" style="cursor: pointer;">
                    <td>{{ index + 1 }}</td>
                    <td>{{ doc.documentName }}</td>
                    <td>{{ doc.documentType }}</td>
                    <td>{{ formatDate(doc.expiryDate) }}</td>
                    <td>
                      <span :class="['badge', statusBadgeClass(doc)]">{{ statusLabel(doc) }}</span>
                    </td>
                    <td class="text-end">
                      <button
                        type="button"
                        class="btn btn-sm btn-style-2 me-1"
                        @click.stop="view(doc)"
                        :disabled="!doc.fileUrl"
                        title="View document"
                      >
                        <i class="fas fa-eye"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click.stop="remove(doc)"><i class="fas fa-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="!loading && documents.length === 0">
                    <td colspan="7" class="text-center text-muted">No compliance documents configured.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  </component>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import AppLayout from '@/components/AppLayout.vue'
import BDLayout from '@/components/BDLayout.vue'
import complianceApi, { API_BASE_URL } from '@/api/compliance'
import 'datatables.net-vue3'
import 'datatables.net-bs5'
import 'datatables.net-buttons-bs5'
import 'datatables.net-responsive-bs5'
import 'jszip'
import 'datatables.net-buttons/js/buttons.html5.js'
import 'datatables.net-buttons/js/buttons.colVis.js'
import 'datatables.net-buttons/js/buttons.print.js'

export default {
  name: 'ComplianceDocuments',
  components: { AppLayout, BDLayout },
  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const documents = ref([])
    const showAddForm = ref(false)
    const supplierDocumentTypes = ref([])

    const blankForm = {
      id: null,
      documentName: '',
      documentType: '',
      expiryDate: '',
      fileUrl: '',
    }

    const form = reactive({ ...blankForm })

    const loadDocuments = async () => {
      loading.value = true
      try {
        const data = await complianceApi.listDocuments()
        const list = data.data || data.items || data.rows || data
        documents.value = Array.isArray(list) ? list : []
        // initialize/re-init datatable after DOM updates
        setTimeout(async () => {
          try {
            // prefer jQuery DataTable if available
            if (window.$ && window.$.fn && window.$.fn.dataTable) {
              const table = window.$('#complianceDocsTable')
              if (table && table.length) {
                try { table.DataTable().destroy() } catch (e) { void 0; }
                table.DataTable({ responsive: true })
              }
              return
            }

            // fallback to datatables.net (no-jQuery)
            try {
              const DataTableModule = await import('datatables.net')
              const DataTable = DataTableModule && DataTableModule.default ? DataTableModule.default : DataTableModule
              const el = document.querySelector('#complianceDocsTable')
              if (el && DataTable) {
                try { if (el._dt) el._dt.destroy(); } catch (_) { void 0; }
                el._dt = new DataTable(el, { responsive: true })
              }
            } catch (e) {
              console.warn('DataTables fallback init failed', e)
            }
          } catch (e) {
            console.warn('DataTable init failed', e)
          }
        }, 50)
      } catch (err) {
        console.error('Failed to load compliance documents', err)
      } finally {
        loading.value = false
      }
    }

    const fetchSupplierDocumentTypes = async () => {
      try {
        const tokenRaw = localStorage.getItem('access_token')
        const token = tokenRaw ? JSON.parse(tokenRaw) : null
        const base = (process.env.VUE_APP_URL || API_BASE_URL).replace(/\/+$/, '')
        const res = await fetch(base + '/supplier-document-type', {
          headers: { 'Accept': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        })
        if (!res.ok) return
        const data = await res.json()
        supplierDocumentTypes.value = Array.isArray(data) ? data : (data.data || [])
      } catch (err) {
        console.error('Failed to load supplier document types', err)
      }
    }

    const resetForm = () => {
      Object.assign(form, blankForm)
    }

    const save = async () => {
      if (!form.documentName) return
      saving.value = true
      try {
        const payload = { ...form }
        let res
        if (payload.id) {
          res = await complianceApi.updateDocument(payload.id, payload)
        } else {
          res = await complianceApi.createDocument(payload)
        }
        if (res && (res.id || res.data)) {
          resetForm()
          loadDocuments()
        }
      } catch (err) {
        console.error('Failed to save compliance document', err)
      } finally {
        saving.value = false
      }
    }

    const edit = (doc) => {
      Object.assign(form, {
        id: doc.id,
        documentName: doc.documentName,
        documentType: doc.documentType,
        expiryDate: doc.expiryDate ? doc.expiryDate.substring(0, 10) : '',
        fileUrl: doc.fileUrl || '',
      })
    }

    const remove = async (doc) => {
      if (!confirm('Delete this document?')) return
      try {
        await complianceApi.deleteDocument(doc.id)
        loadDocuments()
      } catch (err) {
        console.error('Failed to delete compliance document', err)
      }
    }

    const resolveFileUrl = (raw) => {
      if (!raw) return null
      if (/^https?:\/\//i.test(raw)) return raw
      const base = API_BASE_URL.replace(/\/+$/, '')
      if (raw.startsWith('/')) return base + raw
      return base + '/' + raw
    }

    const view = (doc) => {
      const url = resolveFileUrl(doc.fileUrl)
      if (!url) return
      window.open(url, '_blank')
    }

    const formatDate = (val) => {
      if (!val) return 'N/A'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return 'N/A'
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    }

    const statusLabel = (doc) => {
      if (!doc?.expiryDate) return 'Unknown'
      const now = new Date()
      const exp = new Date(doc.expiryDate)
      if (Number.isNaN(exp.getTime())) return 'Unknown'

      const diffDays = (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      if (diffDays < 0) return 'Expired'
      if (diffDays <= 30) return 'Expiring Soon'
      return 'Valid'
    }

    const statusBadgeClass = (doc) => {
      const label = statusLabel(doc)
      switch (label) {
        case 'Valid':
          return 'badge-success'
        case 'Expiring Soon':
          return 'badge-warning'
        case 'Expired':
          return 'badge-danger'
        default:
          return 'badge-secondary'
      }
    }

    const isBusinessDevelopmentMode = computed(() => {
      const stored = localStorage.getItem('active_module') || ''
      return stored === 'business-development'
    })

    const layoutComponent = computed(() =>
      isBusinessDevelopmentMode.value ? BDLayout : AppLayout
    )

    onMounted(() => {
      loadDocuments()
      fetchSupplierDocumentTypes()
    })

    return {
      layoutComponent,
      loading,
      saving,
      documents,
      showAddForm,
      supplierDocumentTypes,
      form,
      save,
      edit,
      remove,
      view,
      resetForm,
      formatDate,
      statusLabel,
      statusBadgeClass,
    }
  },
}
</script>

<style scoped>
</style>
